import { Unsubscribe } from "@firebase/util";
import { makeAutoObservable } from "mobx";
import {
  ContractRowStore,
  ContractStore,
  ContractIntegrationStore as Integration,
  MeasurementAttachmentStore,
  MeasurementListStore,
  MeasurementRowsStore,
  MessageStore,
} from "../";
import MeasurementController from "../../api/controllers/MeasurementController";
import { EMeasurementStatus } from "../../api/enums";
import { GreaterThanOrEqualException } from "../../api/exceptions/InvalidValueException";
import MeasurementModel from "../../api/models/MeasurementModel";
import MeasurementService from "../../api/services/MeasurementService";
import { lang } from "../../i18nLang";

class Store {
  private _currentMeasurementListener: Unsubscribe | undefined = undefined;
  private _currentMeasurement: MeasurementModel | undefined = undefined;
  private _currentMeasurementTotal: number = 0;
  public currentMeasuredTotal: number = 0;
  private _loadingDelete: boolean = false;
  /** Propriedade que guarda os valores acumulados até a medição atual
   * retornados pela API.
   */
  public currentAccumulatedUntilMeasurement: {
    total: number;
    wbs: {
      uid: string;
      quantity: number;
      total: number;
    }[];
  } = {
    total: 0,
    wbs: [],
  };

  constructor() {
    makeAutoObservable(this);
  }

  get currentMeasurement(): MeasurementModel | undefined {
    return this._currentMeasurement;
  }

  get measurementFounded(): boolean {
    return Boolean(this._currentMeasurement);
  }

  get currentMeasurementUid(): string {
    return this._currentMeasurement?.uid || "";
  }

  get currentMeasurementName(): string {
    return this._currentMeasurement?.description || "";
  }

  get currentMeasurementTotal(): number {
    return this._currentMeasurementTotal;
  }

  get allRowsLoaded() {
    return ContractRowStore.allRowsLoaded && MeasurementRowsStore.allRowsLoaded;
  }

  getStatusValues(): string[] {
    return Object.values(EMeasurementStatus);
  }

  get loadingDelete(): boolean {
    return this._loadingDelete;
  }

  reset() {
    this._currentMeasurementListener = undefined;
    this._currentMeasurement = undefined;
    this.currentMeasuredTotal = 0;
    this._loadingDelete = false;
    this.currentAccumulatedUntilMeasurement = {
      total: 0,
      wbs: [],
    };
  }

  subscribeMeasurement(uid: string) {
    const contract = ContractStore.getCurrentContract();
    if (!contract) {
      throw new Error("Contract not found!");
    }

    this.unsubscribeMeasurement();
    const observer = MeasurementService.subscribeMeasurementSnapshot(
      contract,
      uid,
      (measurement) => {
        this.setCurrentMeasurement(measurement);
        this.setCurrentMeasurementTotal(measurement ? measurement.total : 0);
        this.setCurrentMeasuredTotal(contract.measuredTotal);

        if (measurement) {
          ContractRowStore.forceFetchAllContractRows();
          MeasurementRowsStore.subscribeMeasurementRows();
        }
      },
      (measurement) => {
        this.setCurrentMeasurement(measurement);
      }
    );

    if (observer) {
      this._currentMeasurementListener = observer;
    }
  }

  unsubscribeMeasurement() {
    if (this._currentMeasurementListener) {
      this._currentMeasurementListener();
      MeasurementRowsStore.unsubscribeMeasurementRows();
    }

    this.reset();
  }

  createMeasurement() {
    const contractUid = ContractStore.getCurrentContract()?.uid || "";
    return MeasurementController.createMeasurement(contractUid);
  }

  updateMeasurementName(name: string) {
    const measurement = this._currentMeasurement;
    if (!measurement) return;

    return MeasurementService.updateMeasurementDescription(
      measurement,
      name
    ).catch((error) => {
      console.error(error);
      MessageStore.error(error.customMessage || error.message);
    });
  }

  updateMeasurementPeriod(
    measurement: MeasurementModel,
    initial: Date,
    final: Date
  ) {
    return MeasurementService.updateMeasurementPeriod(
      measurement,
      initial,
      final
    ).catch((error) => {
      console.error(error);

      if (
        error instanceof GreaterThanOrEqualException &&
        error.field === "finalDate"
      )
        MessageStore.error(lang("measurements.error.invalidDates"));
      else MessageStore.error(error.customMessage || error.message);
    });
  }

  updateMeasurementStatus(
    measurement: MeasurementModel,
    status: EMeasurementStatus
  ) {
    return MeasurementService.updateMeasurementStatus(
      measurement,
      status
    ).catch((error) => {
      console.error(error);
      MessageStore.error(error.customMessage || error.message);
    });
  }

  updateBillingStatus(measurement: MeasurementModel) {
    if (!Integration.isAuthenticated) return;
    return MeasurementController.updateCurrentBillingOrderStatus(
      Integration.api,
      measurement
    );
  }

  updateBillingInvoices(measurement: MeasurementModel) {
    if (!Integration.isAuthenticated) return;
    MeasurementController.updateBillingOrderInvoices(
      Integration.api,
      measurement
    );
  }

  public async updateBillingOrderIssueDate(measurement: MeasurementModel) {
    if (!Integration.isAuthenticated) return;
    if (
      measurement.hasBillingOrderIssueDate() ||
      !measurement.hasBillingOrder() ||
      measurement.hasBillingOrderError()
    )
      return;

    const billingOrder = { ...measurement.billingOrder };
    const orderIssueDate = await Integration.api.getOrderIssueDate(
      billingOrder.organization!,
      billingOrder.serial,
      billingOrder.code
    );
    if (!orderIssueDate) return;

    return await MeasurementService.updateMeasurementBillingOrder(measurement, {
      ...billingOrder,
      orderIssueDate,
      issueDate: billingOrder.issueDate
        ? billingOrder.issueDate
        : orderIssueDate,
    });
  }

  /** Atualiza o valor do reajuste na medição atual e exibe a Snackbar
   * com a mensagem de sucesso ou erro.
   */
  updateMeasurementReadjustment(readjustValue: number) {
    const measurement = this._currentMeasurement;
    if (!measurement) return;

    return MeasurementService.updateMeasurementReadjustment(
      measurement,
      readjustValue
    )
      .then(() => {
        MessageStore.success(lang("measurementReadjustment.messages.success"));
      })
      .catch((error) => {
        console.error(error);
        MessageStore.error(error.customMessage || error.message);
      });
  }

  private setCurrentMeasurement(measurement: MeasurementModel) {
    this._currentMeasurement = measurement ? measurement : undefined;
  }

  public setCurrentMeasurementTotal(newTotal: number) {
    this._currentMeasurementTotal = newTotal || 0;
  }

  public setCurrentMeasuredTotal(newTotal: number) {
    this.currentMeasuredTotal = newTotal || 0;
  }

  async deleteMeasurement() {
    this._loadingDelete = true;
    const measurement = this.currentMeasurement;
    if (!measurement) return;

    if (measurement.isBilled()) {
      throw new Error("Measurement Billed!");
    }

    return MeasurementAttachmentStore.removeAllMeasurementAttachments(
      measurement
    )
      .then(() => MeasurementRowsStore.deleteMeasurementRows())
      .then(() => MeasurementService.deleteMeasurement(measurement))
      .then(() =>
        MeasurementListStore.removeMeasurementsFromList([measurement])
      );
  }

  async recalculateMeasurement() {
    this._loadingDelete = true;
    const measurement = this.currentMeasurement;
    if (!measurement) return;

    if (!measurement.canEdit()) {
      throw new Error("Measurement Billed!");
    }

    let allRows = MeasurementRowsStore.getAllRows();
    const rows = allRows.getAllRows();

    let dataToSave = {};
    for (let index = rows.length - 1; index >= 0; index--) {
      const row = rows[index];
      if (dataToSave[row.structured]) {
        row.total = dataToSave[row.structured];
      }
      const contractRowData = row.contractRow;
      if (contractRowData) {
        if (dataToSave[contractRowData.parentStructured]) {
          dataToSave[contractRowData.parentStructured] += row.total;
        } else {
          dataToSave[contractRowData.parentStructured] = row.total;
        }
      }
    }
  }

  /** Obtém os dados enviados para faturamento da medição
   * caso haja.
   */
  getMeasurementBillingData(measurement: MeasurementModel) {
    return MeasurementService.getMeasurementBillingData(measurement);
  }

  /** Obtém os valores acumulados até a medição atual. */
  async getAccumulatedUntilCurrentMeasurement() {
    const measurement = MeasurementStore.currentMeasurement;
    if (!measurement) return;

    const result =
      await MeasurementService.fetchCalculateAccumulatedUntilMeasurement(
        measurement.contractUid,
        measurement.code
      );
    this.currentAccumulatedUntilMeasurement = result;
  }
}

const MeasurementStore = new Store();
export default MeasurementStore;
